/**
 * BulkDropzone — drop many resume files at once.
 *
 * Each file is parsed client-side via `parseFile` from engine-ts (PDF via
 * pdfjs, DOCX via mammoth, plain text). Parses run in parallel via
 * `Promise.all` — a 20-resume drop takes <3 seconds on a typical machine
 * since the heavy work is done in pdfjs's own worker.
 */

import { useRef, useState } from "react";
import { FileText, Loader2, UploadCloud, X } from "lucide-react";

import { parseFile } from "@talent-align/engine";

export interface BulkCandidateUpload {
  id: string;
  sourceName: string;
  rawText: string;
  error?: string;
}

interface Props {
  onUpload: (uploads: BulkCandidateUpload[]) => void;
  existingCount: number;
  maxCandidates?: number;
}

type Status =
  | { kind: "idle" }
  | { kind: "parsing"; total: number; done: number }
  | { kind: "error"; message: string };

function makeId(): string {
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function BulkDropzone({ onUpload, existingCount, maxCandidates = 20 }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [dragOver, setDragOver] = useState(false);

  async function handleFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    if (files.length === 0) return;

    const remaining = maxCandidates - existingCount;
    if (remaining <= 0) {
      setStatus({
        kind: "error",
        message: `Max ${maxCandidates} candidates per batch. Remove some first.`,
      });
      return;
    }
    const accepted = files.slice(0, remaining);
    if (files.length > remaining) {
      setStatus({
        kind: "error",
        message: `Accepted ${remaining} of ${files.length} — ${maxCandidates}-candidate cap.`,
      });
      // Still process what we can
    }

    setStatus({ kind: "parsing", total: accepted.length, done: 0 });

    const results: BulkCandidateUpload[] = [];
    let parsed = 0;

    await Promise.all(
      accepted.map(async (file) => {
        try {
          const r = await parseFile(file);
          results.push({
            id: makeId(),
            sourceName: r.source ?? file.name,
            rawText: r.text,
          });
        } catch (err) {
          results.push({
            id: makeId(),
            sourceName: file.name,
            rawText: "",
            error: err instanceof Error ? err.message : "parse failed",
          });
        } finally {
          parsed++;
          setStatus({ kind: "parsing", total: accepted.length, done: parsed });
        }
      }),
    );

    onUpload(results);
    if (status.kind !== "error") setStatus({ kind: "idle" });
    if (inputRef.current) inputRef.current.value = "";
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      void handleFiles(e.dataTransfer.files);
    }
  }

  const remaining = maxCandidates - existingCount;

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`rounded-lg border-2 border-dashed transition ${
        dragOver
          ? "border-purple-500/60 bg-purple-500/5"
          : "border-slate-800/70 bg-slate-900/30 hover:border-blue-500/40"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        className="hidden"
        onChange={(e) => {
          if (e.target.files) void handleFiles(e.target.files);
        }}
      />

      {status.kind === "idle" && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full px-6 py-8 flex flex-col items-center gap-2 text-slate-400 hover:text-slate-200 transition"
        >
          <UploadCloud className="w-8 h-8 text-blue-400" />
          <div className="text-sm font-medium text-slate-200">
            Drop up to {remaining} resumes
          </div>
          <div className="text-xs text-slate-500">
            PDF, DOCX, or text · parsed in your browser · never uploaded anywhere
          </div>
        </button>
      )}

      {status.kind === "parsing" && (
        <div className="w-full px-6 py-8 flex flex-col items-center gap-2">
          <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
          <div className="text-sm text-slate-200">
            Parsing {status.done} of {status.total}...
          </div>
          <div className="w-full max-w-xs h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all"
              style={{ width: `${(status.done / status.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {status.kind === "error" && (
        <div className="w-full px-6 py-4 flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 text-sm text-amber-300 flex-1">
            <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{status.message}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              setStatus({ kind: "idle" });
              if (inputRef.current) inputRef.current.value = "";
            }}
            className="text-slate-500 hover:text-slate-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
